/**
 * Firm KPIs / Health Dashboard
 * Partner-facing firm health overview backed by GET /firm/dashboard.
 */

import { useQuery } from '@tanstack/react-query';
import {
  Loader2, Users, UserCheck, FileText, CheckCircle, AlertTriangle,
  HardDrive, Link2, MessageSquare, TrendingUp, Building2, Activity,
  Bell, Heart, Upload,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import apiClient from '@/services/api/ma-client';

const fmtBytes = (b: number) => {
  const n = Number(b) || 0;
  if (n >= 1073741824) return `${(n / 1073741824).toFixed(1)} GB`;
  if (n >= 1048576) return `${(n / 1048576).toFixed(0)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${n} B`;
};

const healthColor = (level: string) =>
  level === 'excellent' || level === 'good'
    ? 'text-green-600 dark:text-green-400 bg-green-500/10'
    : level === 'fair'
    ? 'text-amber-700 dark:text-amber-400 bg-amber-500/10'
    : 'text-red-600 dark:text-red-400 bg-red-500/10';

const scoreText = (score: number) =>
  score >= 60 ? 'text-green-600 dark:text-green-400'
    : score >= 40 ? 'text-amber-700 dark:text-amber-400'
    : 'text-red-600 dark:text-red-400';

export default function FirmKpiPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['firm-kpi-dashboard'],
    queryFn: async () => {
      const r = await apiClient.get('/firm/dashboard');
      return r.data?.data || r.data;
    },
    retry: 1,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--v4-accent)]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-5 py-6 max-w-lg mx-auto">
        <h2 className="text-xl font-medium text-foreground mb-4">Firm KPIs</h2>
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
          <p className="text-sm text-red-600 dark:text-red-400">
            Could not load dashboard: {(error as any)?.message || 'Unknown error'}
          </p>
        </div>
      </div>
    );
  }

  const summary = data?.summary;
  const clients: any[] = data?.clients || [];
  const recentActivity: any[] = data?.recentActivity || [];
  const xeroHealth: any[] = data?.xeroHealth || [];

  if (!summary) {
    return (
      <div className="px-5 py-6 max-w-lg mx-auto">
        <h2 className="text-xl font-medium text-foreground mb-4">Firm KPIs</h2>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Building2 className="h-10 w-10 text-muted-foreground/40 mb-3" />
          <p className="text-sm text-muted-foreground">No firm data available yet.</p>
        </div>
      </div>
    );
  }

  // Derived KPIs
  const indexRate = summary.totalDocuments > 0
    ? Math.round((summary.totalIndexed / summary.totalDocuments) * 100)
    : 0;
  const atRisk = clients.filter((c) => c.health_level === 'poor' || c.health_level === 'fair').length;
  const avgHealth = clients.length > 0
    ? Math.round(clients.reduce((s, c) => s + (Number(c.health_score) || 0), 0) / clients.length)
    : 0;

  const clientsByType: Record<string, number> = summary.clientsByType || {};
  const clientsByIndustry: Record<string, number> = summary.clientsByIndustry || {};

  // Top / bottom clients by health
  const sortedByHealth = [...clients].sort(
    (a, b) => (Number(b.health_score) || 0) - (Number(a.health_score) || 0),
  );
  const needsAttention = sortedByHealth.slice().reverse().slice(0, 5);

  const stats = [
    { icon: Users, color: 'text-[var(--v4-accent)]', value: summary.totalClients ?? 0, label: 'Total Clients' },
    { icon: UserCheck, color: 'text-green-600 dark:text-green-400', value: summary.activeClients ?? 0, label: 'Active Clients' },
    { icon: FileText, color: 'text-[var(--v4-accent)]', value: summary.totalDocuments ?? 0, label: 'Documents' },
    { icon: CheckCircle, color: 'text-green-600 dark:text-green-400', value: `${indexRate}%`, label: 'Indexed' },
    { icon: Link2, color: 'text-[var(--v4-accent)]', value: summary.xeroConnected ?? 0, label: 'Accounting Linked' },
    { icon: MessageSquare, color: 'text-blue-600 dark:text-blue-400', value: summary.totalConversations ?? 0, label: 'Conversations' },
    { icon: HardDrive, color: 'text-muted-foreground', value: fmtBytes(summary.totalStorage), label: 'Storage Used' },
    { icon: AlertTriangle, color: summary.totalFailed > 0 ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground', value: summary.totalFailed ?? 0, label: 'Failed Docs' },
  ];

  return (
    <div className="px-5 py-6 max-w-lg mx-auto space-y-7">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-light text-foreground flex items-center gap-2">
          <Building2 className="h-6 w-6 text-[var(--v4-accent)]" />
          Firm KPIs
        </h2>
        <p className="text-sm text-muted-foreground mt-1">Practice health at a glance</p>
      </div>

      {/* Health summary banner */}
      <div className="bg-[var(--v4-accent)]/10 border border-[var(--v4-accent)]/20 rounded-2xl p-5 flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-[var(--v4-accent)] flex items-center justify-center shrink-0">
          <Heart className="h-7 w-7 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-muted-foreground">Average client health</p>
          <p className={cn('text-4xl font-bold', scoreText(avgHealth))}>{avgHealth}<span className="text-lg text-muted-foreground font-normal">/100</span></p>
          {atRisk > 0 && (
            <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">{atRisk} client{atRisk === 1 ? '' : 's'} need attention</p>
          )}
        </div>
      </div>

      {/* KPI stat cards */}
      <div className="grid grid-cols-2 gap-3">
        {stats.map((s, i) => (
          <div key={i} className="bg-card border border-border rounded-xl p-4">
            <s.icon className={cn('h-5 w-5 mb-2', s.color)} />
            <p className="text-3xl font-bold text-foreground">{s.value}</p>
            <p className="text-sm text-muted-foreground">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Clients by type */}
      {Object.keys(clientsByType).length > 0 && (
        <div className="space-y-3">
          <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-medium flex items-center gap-1.5">
            <TrendingUp className="h-3.5 w-3.5" /> Clients by Type
          </h3>
          <div className="bg-card border border-border rounded-xl divide-y divide-border">
            {Object.entries(clientsByType)
              .sort((a, b) => b[1] - a[1])
              .map(([type, count]) => (
                <div key={type} className="flex items-center justify-between px-4 py-3">
                  <span className="text-sm text-foreground capitalize">{type.replace(/_/g, ' ')}</span>
                  <span className="text-sm font-semibold text-[var(--v4-accent)]">{count}</span>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Clients by industry */}
      {Object.keys(clientsByIndustry).length > 0 && (
        <div className="space-y-3">
          <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-medium">Top Industries</h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(clientsByIndustry)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 8)
              .map(([industry, count]) => (
                <span
                  key={industry}
                  className="text-xs bg-card border border-border text-muted-foreground px-3 py-1.5 rounded-full"
                >
                  {industry} <span className="text-foreground font-semibold">{count}</span>
                </span>
              ))}
          </div>
        </div>
      )}

      {/* Clients needing attention */}
      {needsAttention.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-medium flex items-center gap-1.5">
            <Bell className="h-3.5 w-3.5" /> Needs Attention
          </h3>
          {needsAttention.map((c) => (
            <div key={c.id} className="flex items-center gap-3 p-4 bg-card border border-border rounded-xl">
              <span className={cn('shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold', healthColor(c.health_level))}>
                {Number(c.health_score) || 0}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{c.display_name || 'Unnamed client'}</p>
                <p className="text-xs text-muted-foreground capitalize">
                  {(c.health_level || 'unknown')} - {c.document_count || 0} docs{c.xero_org_id ? ', accounting linked' : ', no accounting'}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Accounting connection health */}
      {xeroHealth.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-medium flex items-center gap-1.5">
            <Link2 className="h-3.5 w-3.5" /> Accounting Connections
          </h3>
          {xeroHealth.map((x, i) => {
            const expired = x.expires_at && new Date(x.expires_at) < new Date();
            const ok = x.token_status === 'active' && !expired;
            return (
              <div key={i} className="flex items-center gap-3 p-4 bg-card border border-border rounded-xl">
                <span className={cn(
                  'shrink-0 w-2.5 h-2.5 rounded-full',
                  ok ? 'bg-green-500' : 'bg-red-500',
                )} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{x.xero_tenant_name || 'Accounting org'}</p>
                  <p className={cn('text-xs', ok ? 'text-muted-foreground' : 'text-red-600 dark:text-red-400')}>
                    {ok ? 'Connected' : expired ? 'Token expired' : (x.token_status || 'Disconnected')}
                    {x.last_refresh_at && ` - refreshed ${new Date(x.last_refresh_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}`}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Recent activity */}
      {recentActivity.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-medium flex items-center gap-1.5">
            <Activity className="h-3.5 w-3.5" /> Recent Activity
          </h3>
          {recentActivity.slice(0, 8).map((a, i) => {
            const Icon = a.type === 'upload' ? Upload : MessageSquare;
            return (
              <div key={i} className="flex items-center gap-3 py-3 border-b border-border last:border-0">
                <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground truncate">{a.description || (a.type === 'upload' ? 'Document uploaded' : 'Conversation')}</p>
                  {a.client_name && <p className="text-xs text-muted-foreground truncate">{a.client_name}</p>}
                </div>
                {a.event_at && (
                  <span className="text-xs text-muted-foreground shrink-0">
                    {new Date(a.event_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
